/**
 * Small shared helpers for the portal-admin components that render server-supplied data
 * (pk-session-list, pk-invite-panel, pk-plan-card, pk-tenant-switcher).
 */

/** Parses a JSON attribute into an array; anything else (absent, invalid, not an array) → `[]`. */
export function parseJsonArray<T>(raw: string | null): T[] {
    if (!raw) return [];
    try {
        const parsed: unknown = JSON.parse(raw);
        return Array.isArray(parsed) ? (parsed as T[]) : [];
    } catch {
        return [];
    }
}

/** Parses a JSON attribute into a plain object; anything else → `{}`. */
export function parseJsonObject(raw: string | null): Record<string, unknown> {
    if (!raw) return {};
    try {
        const parsed: unknown = JSON.parse(raw);
        return parsed !== null && typeof parsed === 'object' && !Array.isArray(parsed)
            ? (parsed as Record<string, unknown>)
            : {};
    } catch {
        return {};
    }
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sept', 'Oct', 'Nov', 'Dec'];

const pad = (n: number): string => String(n).padStart(2, '0');

/** `12 Sept 2026, 14:05 UTC`-style text for an ISO date; `—` for null / invalid. Deterministic: UTC, fixed English month names, no Intl/ICU dependence. */
export function formatUtc(iso: string | null | undefined): string {
    if (!iso) return '—';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}, ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())} UTC`;
}

/** Replaces `{id}` in a URL template with the URL-escaped id. */
export function fillId(template: string, id: string): string {
    return template.replace('{id}', encodeURIComponent(id));
}

/** `maxSystems` / `max_systems` / `systems` → `Max systems` / `Systems`. */
export function humanizeKey(key: string): string {
    const spaced = key
        .replace(/[_-]+/g, ' ')
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .trim()
        .toLowerCase();
    return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}
